import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import {
  Clock,
  Bell,
  Star,
  TrendingUp,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Send,
  Trash2,
  UserPlus,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Label,
  MultiSelect,
  PageHeader,
  Skeleton,
  Textarea,
} from "@/components/ui";
import { useSearchPatientsMutation, useToast } from "@/hooks";
import {
  addToWaitlist,
  fetchWaitlist,
  checkWaitlistAvailability,
  notifyWaitlistPatient,
  removeFromWaitlist,
  type WaitlistEntry,
  type WaitlistAvailability,
} from "@/services/waitlist";
import {
  fetchNpsDashboard,
  type NpsDashboard,
} from "@/services/nps";

type LoadStatus = "loading" | "ready" | "unavailable";

function StatCard({
  icon: Icon,
  label,
  value,
  gradient,
  subtitle,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  gradient: string;
  subtitle?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${gradient} text-white`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-bold text-foreground">{String(value)}</p>
        {subtitle && <p className="text-[10px] text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}

function NpsGauge({ score }: { score: number }) {
  const color = score >= 50 ? "text-app-success" : score >= 0 ? "text-app-warning" : "text-app-danger";
  const label = score >= 50 ? "Excelente" : score >= 0 ? "Bom" : "Precisa atenção";
  return (
    <div className="flex flex-col items-center">
      <div className={`text-4xl font-bold ${color}`}>{score}</div>
      <p className={`text-xs font-medium ${color}`}>{label}</p>
      <p className="text-[10px] text-muted-foreground mt-0.5">NPS Score</p>
    </div>
  );
}

/**
 * P-07: a read that failed says so and offers the same read again. It never
 * borrows the picture of an empty list (PRODUCT_GOVERNANCE rule 8).
 */
function UnavailablePanel({
  title,
  detail,
  onRetry,
}: {
  title: string;
  detail: string;
  onRetry: () => void;
}) {
  return (
    <div role="alert" className="rounded-xl border border-app-danger/30 bg-app-danger/5 p-5">
      <p className="text-sm font-semibold text-app-danger">{title}</p>
      <p className="mt-1 text-sm text-app-fg-muted">{detail}</p>
      <Button type="button" variant="outline" className="mt-4" onClick={onRetry}>
        Tentar novamente
      </Button>
    </div>
  );
}

export function WaitlistAndNps() {
  const { toast } = useToast();
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [availability, setAvailability] = useState<WaitlistAvailability[]>([]);
  const [nps, setNps] = useState<NpsDashboard | null>(null);
  // The waiting list and the satisfaction panel are two reads and two states:
  // an NPS endpoint that is down must not erase the list of patients waiting.
  const [listStatus, setListStatus] = useState<LoadStatus>("loading");
  const [npsStatus, setNpsStatus] = useState<LoadStatus>("loading");

  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patientId, setPatientId] = useState("");
  const [patientOptions, setPatientOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [notes, setNotes] = useState("");
  const searchPatients = useSearchPatientsMutation();

  const loadList = useCallback(async () => {
    setListStatus("loading");
    try {
      const [entries, slots] = await Promise.all([
        fetchWaitlist(),
        checkWaitlistAvailability(),
      ]);
      setWaitlist(entries);
      setAvailability(slots);
      setListStatus("ready");
    } catch {
      setWaitlist([]);
      setAvailability([]);
      setListStatus("unavailable");
    }
  }, []);

  const loadNps = useCallback(async () => {
    setNpsStatus("loading");
    try {
      setNps(await fetchNpsDashboard());
      setNpsStatus("ready");
    } catch {
      setNps(null);
      setNpsStatus("unavailable");
    }
  }, []);

  useEffect(() => {
    loadList();
    loadNps();
  }, [loadList, loadNps]);

  const searchPatientOptions = useCallback(
    async (term: string) => {
      const response = await searchPatients.mutateAsync({ search: term });
      return (
        response?.items?.map((patient) => ({
          value: patient.id,
          label: patient.person.name,
        })) ?? []
      );
    },
    [searchPatients]
  );

  function openAdd() {
    setPatientId("");
    setNotes("");
    setShowAdd(true);
    // The first page of patients so the select is not empty before typing.
    void searchPatientOptions("").then(setPatientOptions).catch(() => setPatientOptions([]));
  }

  async function handleAdd() {
    if (!patientId) return;
    setSaving(true);
    const created = await addToWaitlist({
      patient_id: patientId,
      notes: notes.trim() || undefined,
    });
    setSaving(false);
    if (created) {
      toast({ title: "Paciente adicionado à lista de espera" });
      setShowAdd(false);
      loadList();
    } else {
      toast({ title: "Erro ao adicionar à lista de espera", variant: "destructive" });
    }
  }

  async function handleNotify(id: string) {
    const ok = await notifyWaitlistPatient(id);
    if (ok) {
      toast({ title: "Paciente notificado sobre vaga" });
      loadList();
    }
  }

  async function handleRemove(id: string) {
    const ok = await removeFromWaitlist(id);
    if (ok) {
      toast({ title: "Removido da lista de espera" });
      loadList();
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* C-11: the page carries the same words as the menu entry. */}
      <PageHeader
        eyebrow="Clínica"
        title="Lista de espera & NPS"
        lede="Quem espera uma vaga na agenda e o que os pacientes atendidos responderam."
        actions={
          <Button variant="petrol" className="gap-2" onClick={openAdd}>
            <UserPlus className="w-4 h-4" /> Adicionar paciente
          </Button>
        }
      />

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adicionar à lista de espera</DialogTitle>
            <DialogDescription>
              O paciente entra como “Aguardando” e aparece aqui quando uma vaga for
              cancelada na agenda.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Paciente</Label>
              <MultiSelect
                options={patientOptions}
                value={patientId}
                onChange={(value) => setPatientId(Array.isArray(value) ? value[0] ?? "" : value)}
                onSearch={searchPatientOptions}
                loading={searchPatients.isLoading}
                placeholder="Selecione um paciente..."
                emptyMessage="Nenhum paciente encontrado"
              />
            </div>
            <div className="space-y-1">
              <Label>Observação</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Ex: prefere o fim da tarde, avisar pelo WhatsApp"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdd} disabled={saving || !patientId}>
              {saving ? "Adicionando..." : "Adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NPS Dashboard */}
      {npsStatus === "loading" ? (
        <Skeleton className="h-40 w-full rounded-xl" aria-label="Carregando NPS" />
      ) : npsStatus === "unavailable" ? (
        <UnavailablePanel
          title="Não foi possível carregar o NPS."
          detail="Sem essa resposta não dá para saber a satisfação dos pacientes atendidos."
          onRetry={() => void loadNps()}
        />
      ) : nps && nps.total_responses > 0 ? (
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-muted-foreground" /> Net Promoter Score
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 items-center">
            <div className="sm:col-span-1 flex justify-center">
              <NpsGauge score={nps.nps_score} />
            </div>
            <div className="sm:col-span-5 grid grid-cols-2 sm:grid-cols-5 gap-3">
              <StatCard icon={Star} label="Média" value={nps.average_score.toFixed(1)} gradient="bg-app-warning" />
              <StatCard icon={ThumbsUp} label="Promotores" value={nps.promoters} gradient="bg-app-success" subtitle="9-10" />
              <StatCard icon={Minus} label="Neutros" value={nps.passives} gradient="bg-app-fg-subtle" subtitle="7-8" />
              <StatCard icon={ThumbsDown} label="Detratores" value={nps.detractors} gradient="bg-app-danger" subtitle="0-6" />
              <StatCard icon={Send} label="Taxa Resposta" value={`${nps.response_rate}%`} gradient="bg-app-info" subtitle={`${nps.total_responses} total`} />
            </div>
          </div>

          {/* Recent NPS responses */}
          {nps.recent_responses.length > 0 && (
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Respostas recentes</p>
              <div className="space-y-1.5">
                {nps.recent_responses.slice(0, 5).map((r) => (
                  <div key={r.id} className="flex items-center gap-3 text-sm">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${r.score >= 9 ? "bg-app-success" : r.score >= 7 ? "bg-app-warning" : "bg-app-danger"}`}>
                      {r.score}
                    </span>
                    <span className="font-medium text-foreground">{r.patient?.name ?? "Paciente"}</span>
                    {r.feedback && <span className="text-xs text-muted-foreground truncate max-w-[300px]">{r.feedback}</span>}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {r.responded_at ? new Date(r.responded_at).toLocaleDateString("pt-BR") : "Pendente"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <EmptyState
          title="Nenhuma pesquisa respondida ainda"
          description="O NPS aparece aqui depois que os pacientes atendidos responderem à pesquisa de satisfação."
        />
      )}

      {listStatus === "loading" ? (
        <div className="space-y-3" aria-busy="true" aria-label="Carregando lista de espera">
          <Skeleton className="h-11 w-full rounded-xl" />
          {[1, 2, 3].map((item) => (
            <Skeleton key={item} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      ) : listStatus === "unavailable" ? (
        <UnavailablePanel
          title="Não foi possível carregar a lista de espera."
          detail="Sem essa resposta não dá para saber quem está aguardando uma vaga na agenda."
          onRetry={() => void loadList()}
        />
      ) : (
        <>
          {/* Availability Alerts */}
          {availability.length > 0 && (
            <div className="rounded-xl border-2 border-app-success/30 bg-app-success/10 p-4">
              <h2 className="text-sm font-semibold text-app-success mb-3 flex items-center gap-2">
                <Bell className="w-4 h-4" /> Vagas disponíveis para lista de espera
              </h2>
              <div className="space-y-2">
                {availability.map((a, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded-lg p-3 border border-border">
                    <div>
                      <p className="text-sm font-medium text-foreground">{a.patient_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {a.available_slot.date} às {a.available_slot.time}{a.available_slot.employee_name ? ` - ${a.available_slot.employee_name}` : ""}
                      </p>
                    </div>
                    <Button size="sm" className="gap-1 bg-app-success hover:bg-app-success text-white" onClick={() => handleNotify(a.waitlist_id)}>
                      <Bell className="w-3 h-3" /> Notificar
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waitlist */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" /> Lista de Espera
                <span className="text-xs text-muted-foreground font-normal">({waitlist.length} pacientes)</span>
              </h2>
            </div>
            {waitlist.length === 0 ? (
              // C-11: the screen entered the menu, so it says what it is for.
              <EmptyState
                title="Nenhum paciente na lista de espera"
                description="Aqui ficam os pacientes que querem ser chamados quando surgir uma vaga na agenda, e a satisfação de quem já foi atendido."
                action={
                  <Button className="gap-2" onClick={openAdd}>
                    <UserPlus className="w-4 h-4" /> Adicionar paciente
                  </Button>
                }
                className="border-0"
              />
            ) : (
              <div className="divide-y divide-border">
                {waitlist.map((entry) => (
                  <div key={entry.id} className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground">{entry.patient?.person?.name ?? "Nome indisponível"}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${entry.status === "waiting" ? "bg-app-warning/10 text-app-warning" : entry.status === "notified" ? "bg-app-info/10 text-app-info" : entry.status === "scheduled" ? "bg-app-success/10 text-app-success" : "bg-app-fg-muted/10 text-app-fg-muted"}`}>
                          {entry.status === "waiting" ? "Aguardando" : entry.status === "notified" ? "Notificado" : entry.status === "scheduled" ? "Agendado" : "Expirado"}
                        </span>
                        {entry.preferred_times && entry.preferred_times.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {entry.preferred_times.map(t => t === "morning" ? "Manhã" : t === "afternoon" ? "Tarde" : "Noite").join(", ")}
                          </span>
                        )}
                        {entry.notes && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{entry.notes}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(entry.status === "waiting" || entry.status === "notified") && (
                        <Button size="sm" variant="outline" asChild>
                          <Link to={`/schedulings?patient=${encodeURIComponent(entry.patient_id)}&openForm=true`}>Agendar</Link>
                        </Button>
                      )}
                      {entry.status === "waiting" && (
                        <Button size="sm" variant="ghost" onClick={() => handleNotify(entry.id)} title="Notificar sobre vaga">
                          <Bell className="w-4 h-4 text-app-info" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleRemove(entry.id)} title="Remover da lista">
                        <Trash2 className="w-4 h-4 text-app-danger" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
